import { __decorate, __param } from 'vscode/external/tslib/tslib.es6.js';
import { Action } from 'vscode/vscode/vs/base/common/actions';
import { CancellationToken, CancellationTokenSource } from 'vscode/vscode/vs/base/common/cancellation';
import { ErrorNoTelemetry } from 'vscode/vscode/vs/base/common/errors';
import { Emitter } from 'vscode/vscode/vs/base/common/event';
import { MarkdownString } from 'vscode/vscode/vs/base/common/htmlContent';
import { Iterable } from 'vscode/vscode/vs/base/common/iterator';
import { Disposable, DisposableMap } from 'vscode/vscode/vs/base/common/lifecycle';
import { revive } from 'vscode/vscode/vs/base/common/marshalling';
import { StopWatch } from 'vscode/vscode/vs/base/common/stopwatch';
import { URI } from 'vscode/vscode/vs/base/common/uri';
import { localizeWithPath } from 'vscode/vscode/vs/nls';
import { CommandsRegistry } from 'vscode/vscode/vs/platform/commands/common/commands';
import { ICommandService } from 'vscode/vscode/vs/platform/commands/common/commands.service';
import { IInstantiationService } from 'vscode/vscode/vs/platform/instantiation/common/instantiation';
import { ILogService } from 'vscode/vscode/vs/platform/log/common/log.service';
import 'vscode/vscode/vs/platform/notification/common/notification';
import { INotificationService } from 'vscode/vscode/vs/platform/notification/common/notification.service';
import { Progress } from 'vscode/vscode/vs/platform/progress/common/progress';
import { IStorageService } from 'vscode/vscode/vs/platform/storage/common/storage.service';
import { ITelemetryService } from 'vscode/vscode/vs/platform/telemetry/common/telemetry.service';
import { IWorkspaceContextService } from 'vscode/vscode/vs/platform/workspace/common/workspace.service';
import { ChatAgentLocation } from 'vscode/vscode/vs/workbench/contrib/chat/common/chatAgents';
import { IChatAgentService } from 'vscode/vscode/vs/workbench/contrib/chat/common/chatAgents.service';
import { ChatModel, ChatWelcomeMessageModel, getHistoryEntriesFromModel, updateRanges } from 'vscode/vscode/vs/workbench/contrib/chat/common/chatModel';
import { ChatRequestAgentPart, ChatRequestAgentSubcommandPart, ChatRequestSlashCommandPart, getPromptText } from 'vscode/vscode/vs/workbench/contrib/chat/common/chatParserTypes';
import { ChatRequestParser } from 'vscode/vscode/vs/workbench/contrib/chat/common/chatRequestParser';
import { InteractiveSessionVoteDirection, ChatCopyKind } from 'vscode/vscode/vs/workbench/contrib/chat/common/chatService';
import { IChatSlashCommandService } from 'vscode/vscode/vs/workbench/contrib/chat/common/chatSlashCommands.service';
import { IChatVariablesService } from 'vscode/vscode/vs/workbench/contrib/chat/common/chatVariables.service';
import { IExtensionService } from 'vscode/vscode/vs/workbench/services/extensions/common/extensions.service';
import Severity$1 from 'vscode/vscode/vs/base/common/severity';

const _moduleId = "vs/workbench/contrib/chat/common/chatServiceImpl";
const serializedChatKey = 'interactive.sessions';
const globalChatKey = 'chat.workspaceTransfer';
const SESSION_TRANSFER_EXPIRATION_IN_MILLISECONDS = 1000 * 60;
const maxPersistedSessions = 25;
let ChatService = class ChatService extends Disposable {
    get transferredSessionData() {
        return this._transferredSessionData;
    }
    constructor(storageService, logService, extensionService, instantiationService, telemetryService, workspaceContextService, chatSlashCommandService, chatVariablesService, chatAgentService, notificationService, commandService) {
        super();
        this.storageService = storageService;
        this.logService = logService;
        this.extensionService = extensionService;
        this.instantiationService = instantiationService;
        this.telemetryService = telemetryService;
        this.workspaceContextService = workspaceContextService;
        this.chatSlashCommandService = chatSlashCommandService;
        this.chatVariablesService = chatVariablesService;
        this.chatAgentService = chatAgentService;
        this.notificationService = notificationService;
        this.commandService = commandService;
        this._sessionModels = this._register(( (new DisposableMap())));
        this._pendingRequests = this._register(( (new DisposableMap())));
        this._onDidPerformUserAction = this._register(( (new Emitter())));
        this.onDidPerformUserAction = this._onDidPerformUserAction.event;
        this._onDidDisposeSession = this._register(( (new Emitter())));
        this.onDidDisposeSession = this._onDidDisposeSession.event;
        this._sessionFollowupCancelTokens = this._register(( (new DisposableMap())));
        const sessionData = storageService.get(serializedChatKey, 1 , '');
        if (sessionData) {
            this._persistedSessions = this.deserializeChats(sessionData);
            const countsForLog = ( (Object.keys(this._persistedSessions))).length;
            if (countsForLog > 0) {
                this.trace('constructor', `Restored ${countsForLog} persisted sessions`);
            }
        }
        else {
            this._persistedSessions = {};
        }
        const transferredData = this.getTransferredSessionData();
        const transferredChat = transferredData?.chat;
        if (transferredChat) {
            this.trace('constructor', `Transferred session ${transferredChat.sessionId}`);
            this._persistedSessions[transferredChat.sessionId] = transferredChat;
            this._transferredSessionData = { sessionId: transferredChat.sessionId, inputValue: transferredData.inputValue };
        }
        this._register(storageService.onWillSaveState(() => this.saveState()));
    }
    isEnabled(location) {
        return this.chatAgentService.getContributedDefaultAgent(location) !== undefined;
    }
    saveState() {
        let allSessions = Array.from(( (this._sessionModels.values())))
            .filter(session => session.initialLocation === ChatAgentLocation.Panel)
            .filter(session => session.getRequests().length > 0);
        allSessions = allSessions.concat(( (Object.values(this._persistedSessions)))
            .filter(session => !( (this._sessionModels.has(session.sessionId))))
            .filter(session => session.requests.length));
        allSessions.sort((a, b) => (b.creationDate ?? 0) - (a.creationDate ?? 0));
        allSessions = allSessions.slice(0, maxPersistedSessions);
        if (allSessions.length) {
            this.trace('onWillSaveState', `Persisting ${allSessions.length} sessions`);
        }
        const serialized = JSON.stringify(allSessions);
        if (allSessions.length) {
            this.trace('onWillSaveState', `Persisting ${serialized.length} chars`);
        }
        this.storageService.store(serializedChatKey, serialized, 1 , 1 );
    }
    notifyUserAction(action) {
        if (action.action.kind === 'vote') {
            this.telemetryService.publicLog2('interactiveSessionVote', {
                direction: action.action.direction === InteractiveSessionVoteDirection.Up ? 'up' : 'down'
            });
        }
        else if (action.action.kind === 'copy') {
            this.telemetryService.publicLog2('interactiveSessionCopy', {
                copyKind: action.action.copyKind === ChatCopyKind.Action ? 'action' : 'toolbar'
            });
        }
        else if (action.action.kind === 'insert') {
            this.telemetryService.publicLog2('interactiveSessionInsert', {
                newFile: !!action.action.newFile
            });
        }
        else if (action.action.kind === 'command') {
            const command = CommandsRegistry.getCommand(action.action.commandButton.command.id);
            const commandId = command ? action.action.commandButton.command.id : 'INVALID';
            this.telemetryService.publicLog2('interactiveSessionCommand', {
                commandId
            });
        }
        else if (action.action.kind === 'runInTerminal') {
            this.telemetryService.publicLog2('interactiveSessionRunInTerminal', {
                languageId: action.action.languageId ?? ''
            });
        }
        this._onDidPerformUserAction.fire(action);
    }
    trace(method, message) {
        if (message) {
            this.logService.trace(`ChatService#${method}: ${message}`);
        }
        else {
            this.logService.trace(`ChatService#${method}`);
        }
    }
    error(method, message) {
        this.logService.error(`ChatService#${method} ${message}`);
    }
    deserializeChats(sessionData) {
        try {
            const arrayOfSessions = revive(JSON.parse(sessionData));
            if (!Array.isArray(arrayOfSessions)) {
                throw ( (new Error('Expected array')));
            }
            const sessions = arrayOfSessions.reduce((acc, session) => {
                for (const request of session.requests) {
                    if (Array.isArray(request.response)) {
                        request.response = ( (request.response.map((response) => {
                            if (typeof response === 'string') {
                                return (
                                     (new MarkdownString(response))
                                );
                            }
                            return response;
                        })));
                    }
                    else if (typeof request.response === 'string') {
                        request.response = [( (new MarkdownString(request.response)))];
                    }
                }
                acc[session.sessionId] = session;
                return acc;
            }, {});
            return sessions;
        }
        catch (err) {
            this.error('deserializeChats', `Malformed session data: ${err}. [${sessionData.substring(0, 20)}${sessionData.length > 20 ? '...' : ''}]`);
            return {};
        }
    }
    getTransferredSessionData() {
        const data = this.storageService.getObject(globalChatKey, 0 , []);
        const workspaceUri = this.workspaceContextService.getWorkspace().folders[0]?.uri;
        if (!workspaceUri) {
            return;
        }
        const thisWorkspace = ( (workspaceUri.toString()));
        const currentTime = Date.now();
        const transferred = data.find(item => ( (URI.revive(item.toWorkspace).toString())) === thisWorkspace && (currentTime - item.timestampInMilliseconds < SESSION_TRANSFER_EXPIRATION_IN_MILLISECONDS));
        const filtered = data.filter(item => ( (URI.revive(item.toWorkspace).toString())) !== thisWorkspace && (currentTime - item.timestampInMilliseconds < SESSION_TRANSFER_EXPIRATION_IN_MILLISECONDS));
        this.storageService.store(globalChatKey, JSON.stringify(filtered), 0 , 1 );
        return transferred;
    }
    getHistory() {
        const sessions = ( (Object.values(this._persistedSessions)))
            .filter(session => session.requests.length > 0);
        sessions.sort((a, b) => (b.creationDate ?? 0) - (a.creationDate ?? 0));
        return (
             (sessions
                .filter(session => !( (this._sessionModels.has(session.sessionId))))
                .filter(session => !session.isImported)
                .map(item => {
                const title = ChatModel.getDefaultTitle(item.requests);
                return {
                    sessionId: item.sessionId,
                    title
                };
            }))
        );
    }
    removeHistoryEntry(sessionId) {
        delete this._persistedSessions[sessionId];
        this.saveState();
    }
    clearAllHistoryEntries() {
        this._persistedSessions = {};
        this.saveState();
    }
    startSession(location, token) {
        this.trace('startSession');
        return this._startSession(undefined, location, token);
    }
    _startSession(someSessionHistory, location, token) {
        const model = this.instantiationService.createInstance(ChatModel, someSessionHistory, location);
        this._sessionModels.set(model.sessionId, model);
        this.initializeSession(model, token);
        return model;
    }
    async initializeSession(model, token) {
        try {
            this.trace('initializeSession', `Initialize session ${model.sessionId}`);
            model.startInitialize();
            await this.extensionService.whenInstalledExtensionsRegistered();
            const defaultAgentData = this.chatAgentService.getContributedDefaultAgent(model.initialLocation) ?? this.chatAgentService.getContributedDefaultAgent(ChatAgentLocation.Panel);
            if (!defaultAgentData) {
                throw ( (new ErrorNoTelemetry('No default agent contributed')));
            }
            await this.extensionService.activateByEvent(`onChatParticipant:${defaultAgentData.id}`);
            const defaultAgent = this.chatAgentService.getActivatedAgents().find(agent => agent.id === defaultAgentData.id);
            if (!defaultAgent) {
                this.notificationService.notify({
                    severity: Severity$1.Error,
                    message: ( localizeWithPath(
                        _moduleId,
                        0,
                        "Chat failed to load. Please ensure that the GitHub Copilot Chat extension is up to date."
                    )),
                    actions: {
                        primary: [
                            ( (new Action('showExtension', ( localizeWithPath(_moduleId, 1, "Show Extension")), undefined, true, () => {
                            return this.commandService.executeCommand('workbench.extensions.action.showExtensionsWithIds', ['GitHub.copilot-chat']);
                        })))
                        ]
                    }
                });
                throw ( (new ErrorNoTelemetry('No default agent registered')));
            }
            const welcomeMessage = model.welcomeMessage ? undefined : (await defaultAgent.provideWelcomeMessage?.(model.initialLocation, token)) ?? undefined;
            const welcomeModel = welcomeMessage && this.instantiationService.createInstance(ChatWelcomeMessageModel, ( (welcomeMessage.map(
                item => typeof item === 'string' ? ( (new MarkdownString(item))) : item
            ))), (await defaultAgent.provideSampleQuestions?.(model.initialLocation, token)) ?? []);
            model.initialize(welcomeModel);
        }
        catch (err) {
            this.trace('startSession', `initializeSession failed: ${err}`);
            model.setInitializationError(err);
            this._sessionModels.deleteAndDispose(model.sessionId);
            this._onDidDisposeSession.fire({ sessionId: model.sessionId, reason: 'initializationFailed' });
        }
    }
    getSession(sessionId) {
        return this._sessionModels.get(sessionId);
    }
    getOrRestoreSession(sessionId) {
        this.trace('getOrRestoreSession', `sessionId: ${sessionId}`);
        const model = this._sessionModels.get(sessionId);
        if (model) {
            return model;
        }
        const sessionData = this._persistedSessions[sessionId];
        if (!sessionData) {
            return undefined;
        }
        if (sessionId === this.transferredSessionData?.sessionId) {
            this._transferredSessionData = undefined;
        }
        return this._startSession(sessionData, sessionData.initialLocation ?? ChatAgentLocation.Panel, CancellationToken.None);
    }
    loadSessionFromContent(data) {
        return this._startSession(data, data.initialLocation ?? ChatAgentLocation.Panel, CancellationToken.None);
    }
    async resendRequest(request, options) {
        const model = this._sessionModels.get(request.session.sessionId);
        if (!model && model !== request.session) {
            throw ( (new Error(`Unknown session: ${request.session.sessionId}`)));
        }
        await model.waitForInitialization();
        if (( (this._pendingRequests.has(request.session.sessionId)))) {
            this.trace('sendRequest', `Session ${request.session.sessionId} already has a pending request`);
            return;
        }
        const location = options?.location ?? model.initialLocation;
        const attempt = options?.attempt ?? 0;
        const enableCommandDetection = !options?.noCommandDetection;
        const implicitVariablesEnabled = options?.implicitVariablesEnabled ?? false;
        const defaultAgent = this.chatAgentService.getDefaultAgent(location);
        this.removeRequest(model.sessionId, request.id);
        await this._sendRequestAsync(model, model.sessionId, request.message, attempt, enableCommandDetection, implicitVariablesEnabled, defaultAgent, location);
    }
    async sendRequest(sessionId, request, options) {
        this.trace('sendRequest', `sessionId: ${sessionId}, message: ${request.substring(0, 20)}${request.length > 20 ? '[...]' : ''}}`);
        if (!request.trim()) {
            this.trace('sendRequest', 'Rejected empty message');
            return;
        }
        const model = this._sessionModels.get(sessionId);
        if (!model) {
            throw ( (new Error(`Unknown session: ${sessionId}`)));
        }
        await model.waitForInitialization();
        if (( (this._pendingRequests.has(sessionId)))) {
            this.trace('sendRequest', `Session ${sessionId} already has a pending request`);
            return;
        }
        const location = options?.location ?? model.initialLocation;
        const attempt = options?.attempt ?? 0;
        const implicitVariablesEnabled = options?.implicitVariablesEnabled ?? false;
        const defaultAgent = this.chatAgentService.getDefaultAgent(location);
        const parsedRequest = this.instantiationService.createInstance(ChatRequestParser).parseChatRequest(sessionId, request, location, options?.parserContext);
        const agent = parsedRequest.parts.find((r) => r instanceof ChatRequestAgentPart)?.agent ?? defaultAgent;
        const agentSlashCommandPart = parsedRequest.parts.find((r) => r instanceof ChatRequestAgentSubcommandPart);
        return {
            responseCompletePromise: this._sendRequestAsync(model, sessionId, parsedRequest, attempt, !options?.noCommandDetection, implicitVariablesEnabled, defaultAgent, location),
            agent,
            slashCommand: agentSlashCommandPart?.command,
        };
    }
    refreshFollowupsCancellationToken(sessionId) {
        this._sessionFollowupCancelTokens.get(sessionId)?.cancel();
        const newTokenSource = ( (new CancellationTokenSource()));
        this._sessionFollowupCancelTokens.set(sessionId, newTokenSource);
        return newTokenSource.token;
    }
    async _sendRequestAsync(model, sessionId, parsedRequest, attempt, enableCommandDetection, implicitVariablesEnabled, defaultAgent, location) {
        const followupsCancelToken = this.refreshFollowupsCancellationToken(sessionId);
        let request;
        const agentPart = 'kind' in parsedRequest ? undefined : parsedRequest.parts.find((r) => r instanceof ChatRequestAgentPart);
        const agentSlashCommandPart = 'kind' in parsedRequest ? undefined : parsedRequest.parts.find((r) => r instanceof ChatRequestAgentSubcommandPart);
        const commandPart = 'kind' in parsedRequest ? undefined : parsedRequest.parts.find((r) => r instanceof ChatRequestSlashCommandPart);
        let gotProgress = false;
        const requestType = commandPart ? 'slashCommand' : 'string';
        const source = ( (new CancellationTokenSource()));
        const token = source.token;
        const sendRequestInternal = async () => {
            const progressCallback = (progress) => {
                if (token.isCancellationRequested) {
                    return;
                }
                gotProgress = true;
                if (progress.kind === 'markdownContent') {
                    this.trace('sendRequest', `Provider returned progress for session ${model.sessionId}, ${progress.content.value.length} chars`);
                }
                else {
                    this.trace('sendRequest', `Provider returned progress: ${JSON.stringify(progress)}`);
                }
                model.acceptResponseProgress(request, progress);
            };
            const stopWatch = ( (new StopWatch(false)));
            const listener = token.onCancellationRequested(() => {
                this.trace('sendRequest', `Request for session ${model.sessionId} was cancelled`);
                this.telemetryService.publicLog2('interactiveSessionProviderInvoked', {
                    timeToFirstProgress: undefined,
                    totalTime: stopWatch.elapsed(),
                    result: 'cancelled',
                    requestType,
                    agent: agentPart?.agent.id ?? '',
                    slashCommand: agentSlashCommandPart ? agentSlashCommandPart.command.name : commandPart?.slashCommand.command,
                    chatSessionId: model.sessionId
                });
                model.cancelRequest(request);
            });
            try {
                let rawResult;
                let agentOrCommandFollowups = undefined;
                if (agentPart || (defaultAgent && !commandPart)) {
                    const agent = (agentPart?.agent ?? defaultAgent);
                    await this.extensionService.activateByEvent(`onChatParticipant:${agent.id}`);
                    const history = getHistoryEntriesFromModel(model, agentPart?.agent.id);
                    const initVariableData = { variables: [] };
                    request = model.addRequest(parsedRequest, initVariableData, attempt, agent, agentSlashCommandPart?.command);
                    const variableData = await this.chatVariablesService.resolveVariables(parsedRequest, model, progressCallback, token);
                    request.variableData = variableData;
                    const promptTextResult = getPromptText(request.message);
                    const updatedVariableData = updateRanges(variableData, promptTextResult.diff);
                    if (implicitVariablesEnabled) {
                        const implicitVariables = agent.defaultImplicitVariables;
                        if (implicitVariables) {
                            const resolvedImplicitVariables = await Promise.all(( (implicitVariables.map(
                                async (v) => ({ name: v, values: await this.chatVariablesService.resolveVariable(v, parsedRequest.text, model, progressCallback, token) })
                            ))));
                            updatedVariableData.variables.push(...resolvedImplicitVariables);
                        }
                    }
                    const requestProps = {
                        sessionId,
                        requestId: request.id,
                        agentId: agent.id,
                        message: promptTextResult.message,
                        command: agentSlashCommandPart?.command.name,
                        variables: updatedVariableData,
                        enableCommandDetection,
                        attempt,
                        location
                    };
                    const agentResult = await this.chatAgentService.invokeAgent(agent.id, requestProps, progressCallback, history, token);
                    rawResult = agentResult;
                    agentOrCommandFollowups = this.chatAgentService.getFollowups(agent.id, requestProps, agentResult, history, followupsCancelToken);
                }
                else if (commandPart && this.chatSlashCommandService.hasCommand(commandPart.slashCommand.command)) {
                    request = model.addRequest(parsedRequest, { variables: [] }, attempt);
                    const history = [];
                    for (const request of model.getRequests()) {
                        if (!request.response) {
                            continue;
                        }
                        history.push({ role: 1 , content: request.message.text });
                        history.push({ role: 2 , content: request.response.response.asString() });
                    }
                    const message = parsedRequest.text;
                    const commandResult = await this.chatSlashCommandService.executeCommand(commandPart.slashCommand.command, message.substring(commandPart.slashCommand.command.length + 1).trimStart(), ( (new Progress(p => {
                        progressCallback(p);
                    }))), history, token);
                    agentOrCommandFollowups = Promise.resolve(commandResult?.followUp);
                    rawResult = {};
                }
                else {
                    throw ( (new Error(`Cannot handle request`)));
                }
                if (token.isCancellationRequested) {
                    return;
                }
                else {
                    if (!rawResult) {
                        this.trace('sendRequest', `Provider returned no response for session ${model.sessionId}`);
                        rawResult = { errorDetails: { message: ( localizeWithPath(_moduleId, 2, "Provider returned null response")) } };
                    }
                    const result = rawResult.errorDetails?.responseIsFiltered ? 'filtered' :
                        rawResult.errorDetails && gotProgress ? 'errorWithOutput' :
                            rawResult.errorDetails ? 'error' :
                                'success';
                    this.telemetryService.publicLog2('interactiveSessionProviderInvoked', {
                        timeToFirstProgress: rawResult.timings?.firstProgress,
                        totalTime: rawResult.timings?.totalElapsed,
                        result,
                        requestType,
                        agent: agentPart?.agent.id ?? '',
                        slashCommand: agentSlashCommandPart ? agentSlashCommandPart.command.name : commandPart?.slashCommand.command,
                        chatSessionId: model.sessionId
                    });
                    model.setResponse(request, rawResult);
                    this.trace('sendRequest', `Provider returned response for session ${model.sessionId}`);
                    model.completeResponse(request);
                    if (agentOrCommandFollowups) {
                        agentOrCommandFollowups.then(followups => {
                            model.setFollowups(request, followups);
                        });
                    }
                }
            }
            finally {
                listener.dispose();
            }
        };
        const rawResponsePromise = sendRequestInternal();
        this._pendingRequests.set(model.sessionId, source);
        rawResponsePromise.finally(() => {
            this._pendingRequests.deleteAndDispose(model.sessionId);
        });
        return rawResponsePromise;
    }
    async removeRequest(sessionId, requestId) {
        const model = this._sessionModels.get(sessionId);
        if (!model) {
            throw ( (new Error(`Unknown session: ${sessionId}`)));
        }
        await model.waitForInitialization();
        model.removeRequest(requestId);
    }
    async addCompleteRequest(sessionId, message, variableData, attempt, response) {
        this.trace('addCompleteRequest', `message: ${message}`);
        const model = this._sessionModels.get(sessionId);
        if (!model) {
            throw ( (new Error(`Unknown session: ${sessionId}`)));
        }
        await model.waitForInitialization();
        const parsedRequest = typeof message === 'string' ?
            this.instantiationService.createInstance(ChatRequestParser).parseChatRequest(sessionId, message) :
            message;
        const request = model.addRequest(parsedRequest, variableData || { variables: [] }, attempt ?? 0);
        if (typeof response.message === 'string') {
            model.acceptResponseProgress(request, { content: ( (new MarkdownString(response.message))), kind: 'markdownContent' });
        }
        else {
            for (const part of response.message) {
                model.acceptResponseProgress(request, part, true);
            }
        }
        model.setResponse(request, response.result || {});
        if (response.followups !== undefined) {
            model.setFollowups(request, response.followups);
        }
        model.completeResponse(request);
    }
    cancelCurrentRequestForSession(sessionId) {
        this.trace('cancelCurrentRequestForSession', `sessionId: ${sessionId}`);
        this._pendingRequests.get(sessionId)?.cancel();
        this._pendingRequests.deleteAndDispose(sessionId);
    }
    clearSession(sessionId) {
        this.trace('clearSession', `sessionId: ${sessionId}`);
        const model = this._sessionModels.get(sessionId);
        if (!model) {
            throw ( (new Error(`Unknown session: ${sessionId}`)));
        }
        if (model.initialLocation === ChatAgentLocation.Panel) {
            this._persistedSessions[sessionId] = model.toJSON();
        }
        this._sessionModels.deleteAndDispose(sessionId);
        this._pendingRequests.get(sessionId)?.cancel();
        this._pendingRequests.deleteAndDispose(sessionId);
        this._onDidDisposeSession.fire({ sessionId, reason: 'cleared' });
    }
    hasSessions() {
        return !!( (Object.values(this._persistedSessions)));
    }
    transferChatSession(transferredSessionData, toWorkspace) {
        const model = Iterable.find(( (this._sessionModels.values())), model => model.sessionId === transferredSessionData.sessionId);
        if (!model) {
            throw ( (new Error(
                `Failed to transfer session. Unknown session ID: ${transferredSessionData.sessionId}`
            )));
        }
        const existingRaw = this.storageService.getObject(globalChatKey, 0 , []);
        existingRaw.push({
            chat: model.toJSON(),
            timestampInMilliseconds: Date.now(),
            toWorkspace: toWorkspace,
            inputValue: transferredSessionData.inputValue,
        });
        this.storageService.store(globalChatKey, JSON.stringify(existingRaw), 0 , 1 );
        this.trace('transferChatSession', `Transferred session ${model.sessionId} to workspace ${( (toWorkspace.toString()))}`);
    }
};
ChatService = ( (__decorate([
    ( (__param(0, IStorageService))),
    ( (__param(1, ILogService))),
    ( (__param(2, IExtensionService))),
    ( (__param(3, IInstantiationService))),
    ( (__param(4, ITelemetryService))),
    ( (__param(5, IWorkspaceContextService))),
    ( (__param(6, IChatSlashCommandService))),
    ( (__param(7, IChatVariablesService))),
    ( (__param(8, IChatAgentService))),
    ( (__param(9, INotificationService))),
    ( (__param(10, ICommandService)))
], ChatService)));

export { ChatService };
